import { useQuery } from "@tanstack/react-query";
import { get, patch, post } from "services/http";

export interface RsvpEventDetails {
  eventId: number;
  eventName: string;
  eventSlug: string;
  eventDate: string; // ISO-8601 instant
  venueName: string;
  eventImageUrl: string | null;
  // True when the event's RSVP capacity is reached, so a new RSVP joins the waitlist.
  waitlisting: boolean;
  // True when the event's RSVP capacity is reached and the event does not
  // waitlist, so no new RSVP can be made for it.
  rsvpFull: boolean;
  // Max seats the player may reserve for this event (events sharing a slug may
  // differ). Null when unlimited, or when fetched without a player ID.
  maxQuantity: number | null;
}

export type RsvpStatus =
  | "CONFIRMED" // reservation exists, approved
  | "WAITLISTED" // reservation exists, waitlisted
  | "OFFERED" // waitlisted reservation with an open spot offer awaiting response
  | "PENDING" // reservation exists, awaiting review
  | "ELIGIBLE" // no reservation; may RSVP
  | "NOT_ELIGIBLE" // no reservation; not allowed
  | "EXPIRED" // no reservation; was eligible but the RSVP window has closed
  | "FULL"; // no reservation; was eligible but capacity is reached and the event does not waitlist

export interface RsvpStatusDetails {
  status: RsvpStatus;
  bookingRequestId: number | null; // set for CONFIRMED/WAITLISTED/PENDING
  eventId: number | null;
  eventName: string | null;
  eventSlug: string;
  planPackageName: string | null; // set when a reservation exists
  quantity: number | null; // current seats reserved; set when a reservation exists
  // Deadline to accept an open waitlist offer; only set when status is OFFERED.
  offerExpiresAt: string | null;
  // True when the event's cancellation cutoff has passed: the player can no
  // longer self-cancel or change this RSVP (admins still can).
  cancellationCutoffPassed: boolean;
  // The event's cancellation cutoff in hours before start; null when none is set.
  cancellationCutoffHours: number | null;
  // Tenant's rendered RSVP disclaimer text; set for the ELIGIBLE state when
  // the tenant has one configured.
  disclaimer: string | null;
  // Tenant's cancellation policy, shown as a titled link opening the full
  // body; set for the ELIGIBLE state when the tenant has one configured.
  cancellationPolicyTitle: string | null;
  cancellationPolicyBody: string | null;
}

export const rsvpEventsQueryKey = (eventSlug: string, playerId?: string) => [
  "fetchRsvpEvents",
  eventSlug,
  playerId ?? null,
];

export const rsvpStatusQueryKey = (eventSlug: string, playerId: string) => [
  "fetchRsvpStatus",
  eventSlug,
  playerId,
];

// When playerId is provided, the backend narrows the list to events the player
// is eligible for (plus any event they already hold an active RSVP on).
export const useFetchRsvpEventsQuery = (
  eventSlug: string,
  enabled: boolean = true,
  playerId?: string,
) => {
  return useQuery(
    rsvpEventsQueryKey(eventSlug, playerId),
    () => {
      return get<RsvpEventDetails[]>({
        url: `/rsvp/${eventSlug}`,
        params: playerId ? { playerId } : undefined,
      });
    },
    {
      enabled: !!eventSlug && enabled,
      retry: false,
    },
  );
};

export const useFetchRsvpStatusQuery = (
  eventSlug: string,
  playerId: string,
) => {
  return useQuery(
    rsvpStatusQueryKey(eventSlug, playerId),
    () => {
      return get<RsvpStatusDetails>({
        url: `/rsvp/${eventSlug}/${playerId}`,
        // The RSVP page renders these as full-page inline messages; the global
        // toast would duplicate them with the raw server message.
        suppressToastCodes: ["ACCOUNT_MEMBER_NOT_FOUND", "EVENT_NOT_FOUND"],
      });
    },
    {
      enabled: !!eventSlug && !!playerId,
      retry: false,
    },
  );
};

export const createRsvpRequest = (params: {
  eventSlug: string;
  playerId: string;
  eventId: number;
  quantity: number;
  // Required (true) when the tenant has a cancellation policy configured;
  // recorded server-side as a user agreement
  policyAccepted?: boolean;
}) => {
  const { eventSlug, playerId, eventId, quantity, policyAccepted } = params;
  return post<RsvpStatusDetails>({
    url: `/rsvp/${eventSlug}/${playerId}`,
    params: { eventId, quantity, policyAccepted },
  });
};

export const updateRsvpQuantityRequest = (params: {
  eventSlug: string;
  playerId: string;
  bookingRequestId: number;
  quantity: number;
}) => {
  const { eventSlug, playerId, bookingRequestId, quantity } = params;
  return patch<RsvpStatusDetails>({
    url: `/rsvp/${eventSlug}/${playerId}/quantity`,
    params: { bookingRequestId, quantity },
  });
};

export const acceptRsvpOfferRequest = (params: {
  eventSlug: string;
  playerId: string;
  bookingRequestId: number;
}) => {
  const { eventSlug, playerId, bookingRequestId } = params;
  return patch<RsvpStatusDetails>({
    url: `/rsvp/${eventSlug}/${playerId}/offer/accept`,
    params: { bookingRequestId },
  });
};

export const cancelRsvpRequest = (params: {
  eventSlug: string;
  playerId: string;
  bookingRequestId: number;
}) => {
  const { eventSlug, playerId, bookingRequestId } = params;
  return patch<RsvpStatusDetails>({
    url: `/rsvp/${eventSlug}/${playerId}/cancel`,
    params: { bookingRequestId },
  });
};
